// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { action, computed, set } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, didUpdate } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismCheckbox, PrismTooltip } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import {
  SHOP_TYPE_DAILY,
  SHOP_TYPE_PERIODIC,
  TYPE_MARKET_INSIGHT,
  TYPE_MARKETPARITY,
  TYPE_MARKETRATES,
  TYPE_PARITY,
  TYPE_PARITY_INSIGHT,
  TYPE_RATES,
  TYPE_REVENUE_INSIGHT,
} from 'frontend-utils/utils/constants/constant-values';
import registerObserver from 'frontend-utils/utils/register-observer';
import GeneralReportScheduleRecipientsMultipleSelect from 'lumen/components/general/report-schedule/recipients-multiple-select';
import GeneralReportScheduleWeekdaySelection from 'lumen/components/general/report-schedule/weekday-selection';
import displayFrequency from 'lumen/helpers/display-frequency';
import displayRateParityFilters from 'lumen/helpers/display-rate-parity-filters';

interface GeneralReportScheduleReportScheduleFormSectionSignature {
  Args: {
    canCreateNewUser: unknown;
    canCreateOtherReports: unknown;
    currentHotel: unknown;
    currentMarket: unknown;
    currentRateChanges: unknown;
    edit: unknown;
    id: unknown;
    invalidUsers: unknown;
    isLoading: unknown;
    maxPersons: unknown;
    reportSectionChanged: unknown;
    reportgroup: unknown;
    selectedWeekdays: unknown;
    shopconfig: unknown;
    subtype: unknown;
    updateReports: unknown;
    user: unknown;
  };
}

export default class GeneralReportScheduleReportScheduleFormSection extends Component<GeneralReportScheduleReportScheduleFormSectionSignature> {
  @service declare intl: Services['intl'];

  @tracked selectedRecipients = [];
  @tracked selectedWeekdays = [];

  isExpanded = false;

  @computed('reportType', 'args.{shopconfig.shopType,currentRateChanges}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get displayRatechanges() {
    if (
      ((this.reportType === SHOP_TYPE_DAILY &&
        // @ts-expect-error: Incorrect type
        this.args.shopconfig?.shopType === TYPE_RATES) ||
        // @ts-expect-error: Incorrect type
        this.args.shopconfig?.shopType === TYPE_MARKETRATES) &&
      // @ts-expect-error: Incorrect type
      this.args.shopconfig?.shopType !== TYPE_MARKETPARITY &&
      this.args.currentRateChanges
    ) {
      return {
        ratechange_days: this.args.currentRateChanges,
      };
    }
    return null;
  }

  @computed(
    'args.shopconfig.{frequency,frequencyType,shopType}',
    'args.subtype',
    'reportType',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formTitle() {
    if (
      // @ts-expect-error: Incorrect type
      this.args.shopconfig?.shopType === TYPE_RATES ||
      // @ts-expect-error: Incorrect type
      this.args.shopconfig?.shopType === TYPE_MARKETRATES
    ) {
      return this.intl.t(`reports.${this.reportType}_report`);
    }
    if (
      // @ts-expect-error: Incorrect type
      this.args.shopconfig?.shopType === TYPE_PARITY ||
      // @ts-expect-error: Incorrect type
      this.args.shopconfig?.shopType === TYPE_MARKETPARITY ||
      // @ts-expect-error: Incorrect type
      this.args.shopconfig?.shopType === TYPE_PARITY_INSIGHT
    ) {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      return this.intl.t(`reports.parity.${this.args.subtype}_otas`);
    }
    // @ts-expect-error: Incorrect type
    if (this.args.shopconfig?.shopType === TYPE_REVENUE_INSIGHT) {
      // @ts-expect-error: Incorrect type
      if (this.args.shopconfig?.frequency === 0) {
        return this.intl.t('revenue_insight.report_daily_summary');
      }
      return this.intl.t('revenue_insight.report_weekly_summary');
    }
    // @ts-expect-error: Incorrect type
    if (this.args.shopconfig?.shopType === TYPE_MARKET_INSIGHT) {
      return this.intl.t('reports.periodic_report');
    }
    return '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDisabled() {
    return this.args.isLoading || this.isRestricted;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMarketInsightReport() {
    // @ts-expect-error: Incorrect type
    return this.args.shopconfig.shopType === TYPE_MARKET_INSIGHT;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isParityInsightReport() {
    // @ts-expect-error: Incorrect type
    return this.args.shopconfig.shopType === TYPE_PARITY_INSIGHT;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRestricted() {
    // @ts-expect-error: Incorrect type
    if (this.args.shopconfig?.shopType === TYPE_MARKETPARITY) {
      // MP Parity reports can only be scheduled for the defaultPersons
      // MP Rates reports can be scheduled for all allowed persons and for OTAs with multipleGuestsRequests #PERSONS-SR
      // @ts-expect-error: Incorrect type
      return this.args.maxPersons !== this.args.currentMarket.defaultPersons;
    }

    return false;
  }

  @computed('args.shopconfig.shopType')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isWeekdayAvailable() {
    // @ts-expect-error: Incorrect type
    return this.args.shopconfig.shopType !== TYPE_PARITY_INSIGHT;
  }

  @computed('args.shopconfig.{frequencyType,shopType}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get reportType() {
    // We fake a daily report for limited parity view since we don't know the day which it needs to send
    if (
      // @ts-expect-error: Incorrect type
      this.args.shopconfig?.frequencyType === SHOP_TYPE_DAILY ||
      // @ts-expect-error: Incorrect type
      this.args.shopconfig?.shopType === TYPE_PARITY_INSIGHT
    ) {
      return SHOP_TYPE_DAILY;
    }
    return SHOP_TYPE_PERIODIC;
  }

  @computed('reportType', 'args.shopconfig.shopLength')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get shopLengthString() {
    if (
      this.reportType === SHOP_TYPE_PERIODIC &&
      // @ts-expect-error: Incorrect type
      this.args.shopconfig?.shopLength
    ) {
      return `${this.intl.t('reports.shopconfig_shopdays', {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        days: this.args.shopconfig?.shopLength,
      })} - `;
    }
    return '';
  }

  constructor() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    registerObserver(
      this,
      ['isExpanded', 'selectedWeekdays.[]', 'selectedRecipients.[]'],
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.isValidFormSectionAfterChange,
    );
  }

  checkFormValidity(): void {
    // The form section is valid when the section is selected,
    // there are weekdays selected and recepients added to the report
    // Or when it is not expanded and there is a reportgroup (this means delete the report)
    const isValid =
      (this.selectedWeekdays?.length &&
        this.isExpanded &&
        this.selectedRecipients?.length) ||
      (!this.isExpanded && this.args.reportgroup);

    if (isValid) {
      // If it is valid, send all necessary data to the form itself
      let hasReportgroupChanged = false;
      if (this.args.reportgroup) {
        // When a reportgroup is present in the section, validity must be more strict
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
        const receiverIds = this.args.reportgroup?.receivers
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
          .map((receiver) => receiver.id)
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .sort()
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .join(',');
        const ids = this.selectedRecipients
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          .map((receiver) => receiver.id)
          .sort()
          .join(',');
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
        const reportDayofWeek = this.args.reportgroup?.dayOfWeek
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .sort()
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .join(',');
        const dayOfWeek = this.selectedWeekdays.sort().join(',');
        // selected receiver ids can not be the same as those in the reportgroup
        // or report days can not be the same
        hasReportgroupChanged =
          receiverIds !== ids || reportDayofWeek !== dayOfWeek;
      }

      const reportObj = {
        config: this.args.shopconfig,
        reportgroup: this.args.reportgroup,
        selectedWeekdays: this.selectedWeekdays,
        selectedRecipients: this.selectedRecipients,
        subtype: this.args.subtype,
        hasReportgroupChanged,
        isExpanded: this.isExpanded,
        id: this.args.id,
      };
      // @ts-expect-error: Incorrect type
      this.args.reportSectionChanged(true, reportObj, this.args.id);
    } else {
      // otherwise send the shopconfig id (always present) to remove the data in the form
      // @ts-expect-error: Incorrect type
      this.args.reportSectionChanged(
        false,
        null,
        this.args.id,
        this.isExpanded,
      );
    }
  }

  isValidFormSectionAfterChange(): void {
    this.checkFormValidity();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  resetReportgroup() {
    this.selectedWeekdays = [];
    this.selectedRecipients = [];
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-assignment
    this.selectedWeekdays = [...(this.args.reportgroup?.dayOfWeek ?? [])];
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.selectedRecipients = [
      ...this.selectedRecipients,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      ...(this.args.reportgroup?.receivers
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        .map((receiver) => ({ id: receiver.id, email: receiver.email }))
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .filter((user) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const disallow =
            // @ts-expect-error: Incorrect type
            this.args.invalidUsers?.length &&
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            this.args.invalidUsers.filter(
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              (invalidUser) => invalidUser.id === user.id,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            ).length;

          return !disallow;
        }) ?? []),
    ];
  }

  @action
  didReceiveReportAttrs(): void {
    const { reportgroup } = this.args;
    if (reportgroup) {
      set(this, 'isExpanded', true);
      this.resetReportgroup();
    } else {
      this.selectedWeekdays = [];
      // @ts-expect-error: Incorrect type
      if (this.args.shopconfig?.shopType === TYPE_PARITY_INSIGHT) {
        this.selectedWeekdays = [
          // @ts-expect-error: Incorrect type
          'mon',
          // @ts-expect-error: Incorrect type
          'tue',
          // @ts-expect-error: Incorrect type
          'wed',
          // @ts-expect-error: Incorrect type
          'thu',
          // @ts-expect-error: Incorrect type
          'fri',
          // @ts-expect-error: Incorrect type
          'sat',
          // @ts-expect-error: Incorrect type
          'sun',
        ];
        // @ts-expect-error: Incorrect type
      } else if (this.args.shopconfig?.shopType === TYPE_MARKET_INSIGHT) {
        // @ts-expect-error: Incorrect type
        this.selectedWeekdays = [...this.selectedWeekdays, 'mon'];
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      } else if (this.args.shopconfig?.dayOfWeek.length !== 7) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.selectedWeekdays = [
          ...this.selectedWeekdays,
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          ...(this.args.shopconfig?.dayOfWeek ?? []),
        ];
      } else {
        this.selectedWeekdays = [
          // @ts-expect-error: Incorrect type
          'mon',
          // @ts-expect-error: Incorrect type
          'tue',
          // @ts-expect-error: Incorrect type
          'wed',
          // @ts-expect-error: Incorrect type
          'thu',
          // @ts-expect-error: Incorrect type
          'fri',
          // @ts-expect-error: Incorrect type
          'sat',
          // @ts-expect-error: Incorrect type
          'sun',
        ];
      }
    }

    this.checkFormValidity();
  }

  @action
  // @ts-expect-error: Incorrect type
  onChangeSelectedRecipients(selectedRecipients): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.selectedRecipients = selectedRecipients;
  }

  @action
  onFormCheckboxClicked(): void {
    if (this.args.reportgroup && this.isExpanded) {
      this.resetReportgroup();
    }
    set(this, 'isExpanded', !this.isExpanded);
  }

  @action
  // @ts-expect-error: Incorrect type
  updateSelectedWeekdays(clickedWeekday): void {
    if (this.isMarketInsightReport) {
      // Only 1 weekday can be selected for MI
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.selectedWeekdays = [clickedWeekday];
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    } else if (this.selectedWeekdays.includes(clickedWeekday)) {
      this.selectedWeekdays = this.selectedWeekdays.filter(
        (weekday) => weekday !== clickedWeekday,
      );
    } else {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.selectedWeekdays = [...this.selectedWeekdays, clickedWeekday];
    }
  }

  <template>
    <div class="form-section">
      <div
        class="report-schedule-non-expanded-row"
        {{didInsert this.didReceiveReportAttrs}}
        {{didUpdate
          this.didReceiveReportAttrs
          @updateReports
          @selectedWeekdays
          @shopconfig
        }}
      >
        <span class="ic-product-tour-schedule-report-checkbox">
          <PrismCheckbox
            @checked={{this.isExpanded}}
            {{! @glint-expect-error: Incorrect type }}
            @disabled={{this.isDisabled}}
            @onChange={{this.onFormCheckboxClicked}}
          >
            <span class="report-schedule-form-title">
              {{this.formTitle}}
            </span>
            {{#unless this.isParityInsightReport}}
              (
              {{this.shopLengthString}}
              {{! @glint-expect-error: Incorrect type }}
              {{displayFrequency this.selectedWeekdays @shopconfig.frequency}}
              {{#if this.displayRatechanges}}
                |&nbsp;{{displayRateParityFilters this.displayRatechanges}}
              {{/if}}
              )
            {{/unless}}
          </PrismCheckbox>
        </span>
        {{#if this.isRestricted}}
          <PrismTooltip @text={{t "reports.no_report_available_persons"}} />
        {{/if}}
      </div>
      {{#if this.isExpanded}}
        <div class="report-schedule-expanded-content-wrapper">
          <div class="report-schedule-divider"></div>
          {{#if this.isWeekdayAvailable}}
            <div>
              <div class="left-column">
                <span class="leading-6">
                  {{t "reports.days_of_week"}}:
                </span>
              </div>
              <div
                class="right-column ic-product-tour-schedule-report-weekday-selection"
              >
                <GeneralReportScheduleWeekdaySelection
                  {{! @glint-expect-error: Incorrect type }}
                  @enabledDays={{@shopconfig.dayOfWeek}}
                  @reportType={{this.reportType}}
                  @reportgroup={{@reportgroup}}
                  @selectedWeekdays={{this.selectedWeekdays}}
                  {{! @glint-expect-error: Incorrect type }}
                  @shopType={{@shopconfig.shopType}}
                  @updateSelectedWeekdays={{this.updateSelectedWeekdays}}
                />
              </div>
              <div class="clear-both"></div>
            </div>
          {{/if}}
          <div class="report-schedule-recipients-wrapper mt-500">
            <div class="left-column">
              <span>
                {{t "reports.recipients"}}:
              </span>
            </div>
            <div class="right-column">
              <GeneralReportScheduleRecipientsMultipleSelect
                @canCreateNewUser={{@canCreateNewUser}}
                @canCreateOtherReports={{@canCreateOtherReports}}
                @currentHotel={{@currentHotel}}
                @currentMarket={{@currentMarket}}
                @edit={{@edit}}
                @onChangeSelectedRecipients={{this.onChangeSelectedRecipients}}
                @selectedRecipients={{this.selectedRecipients}}
                {{! @glint-expect-error: Incorrect type }}
                @shopType={{@shopconfig.shopType}}
                @user={{@user}}
              />
            </div>
            <div class="clear-both"></div>
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
